/**
 * MyPeegu Help Center — video catalogue.
 * Add / edit / remove videos here only. UI picks them up automatically.
 *
 * Supported providers: 'youtube' | 'vimeo' | 'self'
 * For YouTube, prefer embedUrl or videoId.
 */

export const HELP_CATEGORIES = [
	{
		id: 'getting-started',
		label: 'Getting Started',
		// emoji: '🚀',
		description: 'Onboarding and first steps on MyPeegu',
	},
	{
		id: 'academic',
		label: 'Academic',
		// emoji: '🎓',
		description: 'Students, classrooms, and academic workflows',
	},
	{
		id: 'initiations',
		label: 'Initiations',
		// emoji: '🎓',
		description: 'Initiations, one-on-one counselling, and academic workflows',
	},
	{
		id: 'assessment',
		label: 'Assessment',
		// emoji: '🧠',
		description: 'Baselines, assessments, and evaluations',
	},
	// {
	// 	id: 'virtual-counselling',
	// 	label: 'Virtual Counselling',
	// 	// emoji: '❤️',
	// 	description: 'Live and AI-assisted counselling sessions',
	// },
	// {
	// 	id: 'reports',
	// 	label: 'Reports',
	// 	// emoji: '📄',
	// 	description: 'Reports, documentation, and exports',
	// },
	// {
	// 	id: 'administration',
	// 	label: 'Administration',
	// 	// emoji: '⚙️',
	// 	description: 'School setup and admin controls',
	// },
	
]

/**
 * @typedef {'youtube' | 'vimeo' | 'self'} HelpVideoProvider
 * @typedef {{
 *   id: string,
 *   title: string,
 *   description: string,
 *   categoryId: string,
 *   provider: HelpVideoProvider,
 *   embedUrl?: string,
 *   videoId?: string,
 *   src?: string,
 *   thumbnailUrl?: string,
 *   duration?: string,
 *   featured?: boolean,
 * }} HelpVideo
 */

/** @type {HelpVideo[]} */
export const HELP_VIDEOS = [
	// {
	// 	id: 'welcome-to-mypeegu',
	// 	title: 'Welcome to MyPeegu',
	// 	description:
	// 		'A quick introduction to the MyPeegu platform and how it supports student wellbeing.',
	// 	categoryId: 'getting-started',
	// 	provider: 'youtube',
	// 	embedUrl: 'https://www.youtube.com/embed/D5i3Oc-nv8w',
	// 	videoId: 'D5i3Oc-nv8w',
	// 	featured: true,
	// },
	// {
	// 	id: 'dashboard-overview',
	// 	title: 'Dashboard Overview',
	// 	description:
	// 		'Learn how to navigate the dashboard, menus, and key shortcuts for daily work.',
	// 	categoryId: 'getting-started',
	// 	provider: 'youtube',
	// 	embedUrl: 'https://www.youtube.com/embed/NDUSbCUoxuU',
	// 	videoId: 'NDUSbCUoxuU',
	// 	featured: true,
	// },
	// {
	// 	id: 'virtual-counselling-walkthrough',
	// 	title: 'Virtual Counselling Walkthrough',
	// 	description:
	// 		'Step-by-step guide to starting and managing a virtual counselling session.',
	// 	categoryId: 'virtual-counselling',
	// 	provider: 'youtube',
	// 	embedUrl: 'https://www.youtube.com/embed/OSx6spqgpHA',
	// 	videoId: 'OSx6spqgpHA',
	// 	featured: true,
	// },
	{
		id: 'student-manual',
		title: 'Student Manual',
		description:
			'Learn how to add and manage students in MyPeegu using the student workflow.',
		categoryId: 'academic',
		provider: 'youtube',
		embedUrl: 'https://www.youtube.com/embed/U57ZNIXRGCM',
		videoId: 'U57ZNIXRGCM',
		duration: '0:20',
		featured: true,
	},
	{
		id: 'send-checklist',
		title: 'Send Checklist',
		description:
			'Walkthrough of sending checklists to support student follow-up and documentation.',
		categoryId: 'assessment',
		provider: 'youtube',
		featured: false,
		embedUrl: 'https://www.youtube.com/embed/OGwNjczJJfo',
		videoId: 'OGwNjczJJfo',
		duration: '0:31',
	},
	{
		id: 'one-on-one-counselling',
		title: 'One-to-One Counselling',
		description:
			'Guide to starting and managing a one-to-one counselling session in MyPeegu.',
		categoryId: 'initiations',
		provider: 'youtube',
		embedUrl: 'https://www.youtube.com/embed/YJh0cyyaie8',
		videoId: 'YJh0cyyaie8',
		duration: '0:51',
		featured: true,
	},
	{
		id: 'observation',
		title: 'Observation',
		description:
			'How to record and review student observations during counselling workflows.',
		categoryId: 'assessment',
		provider: 'youtube',
		embedUrl: 'https://www.youtube.com/embed/h1mlLNsMXy0',
		videoId: 'h1mlLNsMXy0',
		duration: '2:12',
	},
	{
		id: 'iep',
		title: 'IEP',
		description:
			'Create and manage Individual Education Plans (IEP) for student support.',
		categoryId: 'assessment',
		provider: 'youtube',
		embedUrl: 'https://www.youtube.com/embed/JNjkzORYpcM',
		videoId: 'JNjkzORYpcM',
		duration: '0:47',
		featured: true,
	},
	{
		id: 'baseline',
		title: 'Baseline',
		description:
			'Run and review baseline assessments to understand student starting points.',
		categoryId: 'assessment',
		provider: 'youtube',
		embedUrl: 'https://www.youtube.com/embed/7Xx4r4oZ1mY',
		videoId: '7Xx4r4oZ1mY',
		duration: '0:41',
	},
]

export const getCategoryById = (categoryId) =>
	HELP_CATEGORIES.find((c) => c.id === categoryId) || null

export const getYoutubeThumbnail = (video) => {
	if (video?.thumbnailUrl) return video.thumbnailUrl
	const id = video?.videoId || extractYoutubeId(video?.embedUrl)
	if (!id) return null
	return `https://img.youtube.com/vi/${id}/hqdefault.jpg`
}

export const extractYoutubeId = (url = '') => {
	if (!url) return null
	const match = String(url).match(
		/(?:embed\/|v=|youtu\.be\/|shorts\/)([a-zA-Z0-9_-]{6,})/,
	)
	return match?.[1] || null
}

export const resolveVideoEmbedUrl = (video, options = {}) => {
	if (!video) return ''
	const autoplay = Boolean(options.autoplay)

	const withAutoplay = (url, provider) => {
		if (!url || !autoplay) return url
		try {
			const parsed = new URL(url, window?.location?.origin || 'https://www.youtube.com')
			if (provider === 'youtube') {
				parsed.searchParams.set('autoplay', '1')
				parsed.searchParams.set('rel', '0')
				parsed.searchParams.set('modestbranding', '1')
				parsed.searchParams.set('playsinline', '1')
			} else if (provider === 'vimeo') {
				parsed.searchParams.set('autoplay', '1')
			}
			return parsed.toString()
		} catch {
			const join = url.includes('?') ? '&' : '?'
			return `${url}${join}autoplay=1`
		}
	}

	if (video.provider === 'youtube') {
		const base = video.embedUrl
			|| (video.videoId ? `https://www.youtube.com/embed/${video.videoId}` : '')
		return withAutoplay(base, 'youtube')
	}
	if (video.provider === 'vimeo') {
		const base = video.embedUrl
			|| (video.videoId ? `https://player.vimeo.com/video/${video.videoId}` : '')
		return withAutoplay(base, 'vimeo')
	}
	if (video.provider === 'self') return video.src || video.embedUrl || ''
	return withAutoplay(video.embedUrl || '', 'youtube')
}
